import { clamp, isEmpty } from 'lodash';
import { Fragment } from 'react';

export interface PopupContentProps {
  header: string;
  headcount?: string;
  postings?: string;
  timeToFill?: string;
  salaryDist?: string[];
  marketTightness?: string;
  isLoading: boolean;
  height?: number;
  showMarketTightness?: boolean;
}

const HeadcountRow = ({
  headcount,
  isLoading,
}: {
  headcount?: string;
  isLoading: boolean;
}) => {
  if (!headcount) return null;
  return (
    <div className="info-item" data-testid="td-tooltip-headcount">
      <div>Headcount</div>
      <div>
        {isLoading ? '' : headcount}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-headcount" />
        </div>
      </div>
    </div>
  );
};

const PostingsRow = ({
  postings,
  isLoading,
}: {
  postings?: string;
  isLoading: boolean;
}) => {
  if (!postings) return null;
  return (
    <div className="info-item" data-testid="td-tooltip-postings">
      <div>Postings</div>
      <div>
        {isLoading ? '' : postings}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-postings" />
        </div>
      </div>
    </div>
  );
};

const TimeToFillRow = ({
  timeToFill,
  isLoading,
}: {
  timeToFill?: string;
  isLoading: boolean;
}) => {
  if (!timeToFill) return null;
  return (
    <div className="info-item">
      <div>Time to Fill</div>
      <div data-testid="td-tooltip-time-to-fill">
        {isLoading ? '' : `${timeToFill} days`}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-time-to-fill" />
        </div>
      </div>
    </div>
  );
};

const SalaryDistSection = ({
  salaryDist,
  isLoading,
}: {
  salaryDist?: string[];
  isLoading: boolean;
}) => {
  if (!salaryDist) return null;
  return (
    <div className="plot-container">
      <div className="plot-box plot-box-bell">
        <div className="plot-title">Salary</div>
        <div className="plot-item">
          <div style={{ display: isLoading ? 'none' : 'block' }}>
            {isEmpty(salaryDist) ? (
              <div className="plot-no-data">No data at this time</div>
            ) : (
              <Fragment>
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 155 35"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M77.5 1C55.641 1 43.7179 34 0 34H155C109.295 34 97.3718 1 77.5 1Z"
                    fill="url(#paint0_linear_3541_2187)"
                  />
                  <path
                    d="M77 1V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M100 12V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M123 27V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M54 12V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M31 27V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M155 34L4.84173e-06 34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M0 34C43.7179 34 55.641 1 77.5 1C97.3718 1 109.295 34 155 34"
                    stroke="#48AFF4"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_3541_2187"
                      x1="77.5"
                      y1="1"
                      x2="77.5"
                      y2="34"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#48AFF4" stopOpacity="0.5" />
                      <stop offset="1" stopColor="#48AFF4" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="plot-axis">
                  <div
                    className="plot-axis-label"
                    data-testid="td-tooltip-salary-dist"
                  >
                    {salaryDist[0] ?? ''}
                  </div>
                  <div
                    className="plot-axis-label"
                    data-testid="td-tooltip-salary-dist"
                  >
                    {salaryDist[1] ?? ''}
                  </div>
                  <div
                    className="plot-axis-label"
                    data-testid="td-tooltip-salary-dist"
                  >
                    {salaryDist[2] ?? ''}
                  </div>
                </div>
              </Fragment>
            )}
          </div>
          <div
            className="h46"
            style={{ display: isLoading ? 'block' : 'none' }}
          >
            <div className="skeleton skeleton-line br2 skeleton-salary-dist" />
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketTightnessSection = ({
  marketTightness,
  isLoading,
}: {
  marketTightness?: string;
  isLoading: boolean;
}) => {
  if (!marketTightness) return null;

  const parsedMarketTightness =
    marketTightness === '−−' ? 0 : parseInt(marketTightness, 10) || 0;
  const clampedMarketTightness = clamp(parsedMarketTightness, 0, 100);

  return (
    <div className="plot-container" data-testid="td-tooltip-market-tightness">
      <div className="plot-box">
        <div className="plot-title">Market Tightness</div>
        <div className="plot-item">
          <div
            className="slider-popover"
            style={{ left: `${clampedMarketTightness}%` }}
          >
            {isLoading ? '' : `${clampedMarketTightness}%`}
          </div>
          <div style={{ display: isLoading ? 'none' : 'block' }}>
            <input
              className="slider"
              type="range"
              min="1"
              max="100"
              value={clampedMarketTightness}
              readOnly
            />
          </div>
          <div
            className="h10full"
            style={{
              display: isLoading ? 'block' : 'none',
              marginTop: '8px',
            }}
          >
            <div className="skeleton skeleton-line brfull skeleton-market-tightness" />
          </div>
        </div>
      </div>
    </div>
  );
};

export function PopupContent({
  header,
  headcount,
  postings,
  timeToFill,
  salaryDist,
  marketTightness,
  isLoading,
  height,
  showMarketTightness = false,
}: PopupContentProps) {
  return (
    <div
      className="tooltip-td"
      data-testid="td-tooltip-container"
      style={typeof height === 'number' ? { height } : undefined}
    >
      <div className="header">{header}</div>
      <div className="list-info">
        <HeadcountRow headcount={headcount} isLoading={isLoading} />
        <PostingsRow postings={postings} isLoading={isLoading} />
        <TimeToFillRow timeToFill={timeToFill} isLoading={isLoading} />
        <hr className="divider" />
        {showMarketTightness ? (
          <MarketTightnessSection
            marketTightness={marketTightness}
            isLoading={isLoading}
          />
        ) : null}
        <SalaryDistSection salaryDist={salaryDist} isLoading={isLoading} />
      </div>
    </div>
  );
}
